import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useDrag } from 'react-dnd';
import { selectSearchParams } from '../../app/store';
import { setStoriesAllExpanded } from './slice';
import { api } from '../../app/http-common';
import { timeAgo } from './transformers';
import { mapFeedStory } from './feedStoryShape';
import { useCanEdit, useIsAdmin, useHasFeature } from '../../hooks/useFrontegg';
import useIdleCallback from '../../hooks/useIdleCallback';
import { useConfig } from '../../contexts/FrontendConfigContext';
import StoriesSkeleton from './StoriesSkeleton';
import { STORY_BUCKET_TYPE } from './BucketSidebar';
import AddToBucketModal from './AddToBucketModal';
import './StoriesFeed.css';

/* ---- SVG Icon Components ---- */
const ChevronRight = ({ size = 16 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="9 18 15 12 9 6" />
    </svg>
);
const ChevronDown = ({ size = 16 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="6 9 12 15 18 9" />
    </svg>
);
const CheckIcon = ({ size = 12 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="20 6 9 17 4 12" />
    </svg>
);
const IrrelevantIcon = ({ size = 12 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" />
        <line x1="4.93" y1="4.93" x2="19.07" y2="19.07" />
    </svg>
);

const MergeIcon = ({ size = 12 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M6 3v12" /><path d="M18 9a3 3 0 100-6 3 3 0 000 6z" /><path d="M6 21a3 3 0 100-6 3 3 0 000 6z" /><path d="M15 6c-6 0-6 12 0 12" />
    </svg>
);
const ShareIcon = ({ size = 14 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/>
        <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
    </svg>
);
const CopyIcon = ({ size = 14 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012-2v1"/>
    </svg>
);
const BucketIcon = ({ size = 12 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M2 7l10 5 10-5"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/>
    </svg>
);

/* ---- Helper Functions ---- */
const getBestSourceUrl = (articles) => {
    if (!articles || articles.length === 0) return '';
    const preferred = articles.find(a => a.isPreferredPublisher && a.url);
    if (preferred) return preferred.url;
    const english = articles.find(a => (a.lang === 'eng' || a.lang === 'en' || !a.lang) && a.url);
    if (english) return english.url;
    const first = articles.find(a => a.url);
    return first?.url || '';
};

const getExcerptText = (excerpt) => {
    if (!excerpt) return null;
    if (typeof excerpt === 'string') return excerpt;
    if (!excerpt.text) return null;
    let text = excerpt.text;
    if (typeof text !== 'string') return text;
    text = text.trim();
    if ((text.startsWith('"') && text.endsWith('"')) || (text.startsWith("'") && text.endsWith("'"))) {
        text = text.slice(1, -1);
    }
    if (text.startsWith('[') || text.startsWith('{')) {
        try {
            const parsed = JSON.parse(text);
            if (Array.isArray(parsed) && parsed.length > 0) {
                const extracted = parsed.map(item => item.translation || item.text || item.original_text || (typeof item === 'string' ? item : '')).filter(Boolean).join(' ');
                if (extracted) return extracted;
            } else if (typeof parsed === 'object' && parsed !== null) {
                return parsed.translation || parsed.text || parsed.original_text || text;
            }
        } catch (e) { /* not JSON */ }
    }
    return text;
};

const getSentimentLabel = (sentiment) => {
    if (sentiment == null) return { label: 'Unknown', color: '#9ca3af' };
    if (sentiment >= 0.6) return { label: 'Positive', color: '#22c55e' };
    if (sentiment <= 0.4) return { label: 'Negative', color: '#ef4444' };
    return { label: 'Neutral', color: '#6b7280' };
};

const getRankingLabel = (ranking) => {
    if (ranking == null) return null;
    if (ranking >= 80) return { label: 'Very High', color: '#dc2626' };
    if (ranking >= 60) return { label: 'High', color: '#ea580c' };
    if (ranking >= 40) return { label: 'Medium', color: '#ca8a04' };
    return { label: 'Low', color: '#6b7280' };
};

const formatShares = (totalShares) => {
    if (!totalShares) return null;
    const total = (totalShares.facebook || 0) + (totalShares.twitter || 0) + (totalShares.linkedin || 0);
    if (total === 0) return null;
    if (total >= 1000000) return `${(total / 1000000).toFixed(1)}M`;
    if (total >= 1000) return `${(total / 1000).toFixed(1)}K`;
    return total.toString();
};

const formatFeedSources = (feedSources) => {
    if (!feedSources || feedSources.length === 0) return null;
    const labels = feedSources.map(source => {
        if (source === 'newsapi_preferred') return '⭐ Preferred';
        if (source === 'newsapi_expansion') return '📁 Category';
        if (source === 'newsapi_expansion_concepts') return '💡 Concept';
        if (source === 'newsapi_feed_pr') return 'PR';
        if (source === 'newsapi_feed') return 'NewsAPI';
        return source;
    });
    return labels.join(', ');
};

const SkeletonLoader = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {[100, 92, 75].map((w, i) => (
            <div key={i} style={{
                height: '14px', width: `${w}%`, borderRadius: '4px',
                animation: 'shimmer 1.5s ease-in-out infinite', animationDelay: `${i * 0.1}s`,
                background: 'linear-gradient(90deg, #e0e0e0 25%, #f0f0f0 50%, #e0e0e0 75%)', backgroundSize: '200% 100%'
            }} />
        ))}
        <style>{`@keyframes shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }`}</style>
    </div>
);

/* ---- StoryRow Component ---- */
function StoryRow({ story, onExpand, onShare, onMerge, canEdit, isAdmin, isNew, alreadyDone, isExpanded, onToggleExpand, isMergeSelected, onToggleMergeSelect, showToastNotification, hasSocialPosts, activeCategory, onMarkIrrelevant, isIrrelevant, collectionScope }) {
    const [{ isDragging }, dragRef] = useDrag({
        type: STORY_BUCKET_TYPE,
        item: { storyId: story._id, storyTitle: story.title },
        collect: (monitor) => ({ isDragging: monitor.isDragging() }),
        canDrag: () => canEdit,
    });

    const [localAlreadyDone, setLocalAlreadyDone] = useState(alreadyDone);
    const [isPreviewVisible, setIsPreviewVisible] = useState(false);
    const [translatedExcerpt, setTranslatedExcerpt] = useState(null);
    const [isTranslating, setIsTranslating] = useState(false);
    const [generatingSocialPost, setGeneratingSocialPost] = useState(false);
    const [addToBucketOpen, setAddToBucketOpen] = useState(false);

    useEffect(() => { setLocalAlreadyDone(alreadyDone); }, [alreadyDone]);

    const handleNext = (e) => {
        e.stopPropagation();
        onExpand(story);
    };

    const handleRowClick = () => {
        onToggleExpand(story._id);
    };

    const handleShowPreview = useCallback(async (e) => {
        e.stopPropagation();
        const excerptRaw = story.rawExcerpt || story.excerpt;
        const excerptText = getExcerptText(excerptRaw);
        const isEnglish = story.rawExcerpt?.isEnglish !== false;

        if (!isPreviewVisible && excerptText && !isEnglish && !translatedExcerpt) {
            setIsPreviewVisible(true);
            setIsTranslating(true);
            try {
                const result = await api.translateFast({ texts: [excerptText], target: 'en' });
                const translations = Array.isArray(result) ? result : result?.translations;
                if (translations && translations.length > 0) {
                    let translated = translations[0];
                    if (typeof translated === 'string') {
                        translated = translated.trim();
                        if ((translated.startsWith('"') && translated.endsWith('"')) || (translated.startsWith("'") && translated.endsWith("'"))) {
                            translated = translated.slice(1, -1);
                        }
                        if (translated.startsWith('[') || translated.startsWith('{')) {
                            try {
                                const parsed = JSON.parse(translated);
                                if (Array.isArray(parsed) && parsed.length > 0) {
                                    translated = parsed.map(item => item.translation || item.text || item.original_text || (typeof item === 'string' ? item : '')).filter(Boolean).join(' ');
                                } else if (typeof parsed === 'object' && parsed !== null) {
                                    translated = parsed.translation || parsed.text || parsed.original_text || translated;
                                }
                            } catch (e) { /* not JSON */ }
                        }
                    }
                    setTranslatedExcerpt(translated);
                } else {
                    setTranslatedExcerpt(excerptText);
                }
            } catch (err) {
                console.error('Translation error:', err);
                setTranslatedExcerpt(excerptText);
            } finally {
                setIsTranslating(false);
            }
        } else {
            setIsPreviewVisible(!isPreviewVisible);
        }
    }, [isPreviewVisible, story.rawExcerpt, story.excerpt, translatedExcerpt]);

    const handleMarkCompleted = async (e) => {
        e.stopPropagation();
        const newStatus = !localAlreadyDone;
        await api.setStoryCompleted({ storyId: story._id, completed: newStatus });
        setLocalAlreadyDone(newStatus);
    };

    const handleMergeSelect = (e) => {
        e.stopPropagation();
        if (onToggleMergeSelect) onToggleMergeSelect(story);
    };

    const handleSharePublicStory = (e) => {
        e.stopPropagation();
        const firstEnglishArticle = story.articles?.find(a => a.lang === 'eng') || story.articles?.[0];
        const storyUrl = story.slug ? `${window.location.origin}/s/${story.slug}` : firstEnglishArticle?.url || '';
        const storyTitle = story.title || '';
        const articleHeadline = firstEnglishArticle?.subject || firstEnglishArticle?.title || '';
        const sourceUrl = getBestSourceUrl(story.articles);
        const textToCopy = `${storyTitle}\n${articleHeadline}\n${storyUrl}${sourceUrl ? `\n${sourceUrl}` : ''}`;
        navigator.clipboard.writeText(textToCopy)
            .then(() => { if (showToastNotification) showToastNotification('Link copied to clipboard!'); })
            .catch(err => { console.error('Failed to copy:', err); if (showToastNotification) showToastNotification('Failed to copy link', 'error'); });
    };

    const handleOpenAddToBucket = (e) => {
        e.stopPropagation();
        setAddToBucketOpen(true);
    };

    const handleCreateSocialPost = async (e) => {
        e.stopPropagation();
        setGeneratingSocialPost(true);
        try {
            const data = await api.generateSocialPost({ storyId: story._id, platform: 'instagram', language: 'en' });
            if (data?.post?._id) {
                window.open(`/social-posts/${data.post._id}`, '_blank');
            }
        } catch (err) {
            console.error('Failed to generate social post:', err);
            if (showToastNotification) showToastNotification('Failed to generate social post. Please try again.', 'error');
        } finally {
            setGeneratingSocialPost(false);
        }
    };

    const excerptText = getExcerptText(story.rawExcerpt || story.excerpt);
    const storyCardArticleCount = story.totalArticleCount ?? story.articleCount ?? story.articles?.length ?? 0;
    const showArticleCount = Number.isFinite(storyCardArticleCount) && storyCardArticleCount > 0;
    // A Signal, presented as a story like any other but marked. `signal` is
    // attached by /stories_search and is null for an ordinary story.
    const signal = story.signal || null;

    return (
        <div
            ref={canEdit ? dragRef : undefined}
            id={`story-${story._id}`}
            className={`sf-story-row${isNew ? ' sf-new-story' : ''}${isExpanded ? ' sf-story-row-expanded' : ''}${signal ? ' sf-signal-row' : ''}`}
            draggable={canEdit}
            onClick={handleRowClick}
            style={isDragging ? { opacity: 0.5 } : undefined}
        >
            <span className={`sf-row-chevron${isExpanded ? ' sf-row-chevron-open' : ''}`}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="9 18 15 12 9 6"></polyline>
                </svg>
            </span>
            <div className="sf-row-content">
                <span
                    className={`sf-row-title${localAlreadyDone ? ' already-done' : ''}${isExpanded ? ' sf-row-title-expanded' : ''}`}
                    onClick={isExpanded ? handleNext : undefined}
                    role={isExpanded ? 'button' : undefined}
                    tabIndex={isExpanded ? 0 : undefined}
                    onKeyDown={isExpanded ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleNext(e); } } : undefined}
                >
                    {signal && (
                        <span
                            className="sf-row-trending"
                            title={signal.articlesInWindow && signal.windowMinutes
                                ? `${signal.articlesInWindow} articles in ${signal.windowMinutes} minutes`
                                : 'Spotted as a fast-growing story'}
                        >
                            Trending
                        </span>
                    )}
                    {story.title}
                </span>

                {/* Expanded panel */}
                <div className={`sf-row-expand-panel${isExpanded ? ' sf-row-expand-panel-open' : ''}`}>
                    {/* Languages */}
                    {story.languages && (
                        <div className="sf-row-languages">
                            {story.languages.split(',').map((lang, idx, arr) => (
                                <span key={idx} className="sf-row-lang-text">{lang.trim()}{idx < arr.length - 1 ? ', ' : ''}</span>
                            ))}
                        </div>
                    )}

                    {/* Metadata line */}
                    <div className="sf-row-metadata">
                        {story.maxRanking != null && (() => {
                            const rankInfo = getRankingLabel(story.maxRanking);
                            return rankInfo ? (
                                <span className="sf-row-meta-item">
                                    📊 <span style={{ color: rankInfo.color, fontWeight: '600' }}>{rankInfo.label}</span>
                                </span>
                            ) : null;
                        })()}
                        {story.dominantSentiment != null && (() => {
                            const sentimentInfo = getSentimentLabel(story.dominantSentiment);
                            const emoji = story.dominantSentiment >= 0.6 ? '😊' : story.dominantSentiment <= 0.4 ? '😟' : '😐';
                            return (
                                <span className="sf-row-meta-item">
                                    {emoji} <span style={{ color: sentimentInfo.color }}>{sentimentInfo.label}</span>
                                </span>
                            );
                        })()}
                        {story.feedSources && formatFeedSources(story.feedSources) && (
                            <>
                                <span className="sf-row-meta-sep">|</span>
                                <span className="sf-row-meta-item">
                                    📡 {formatFeedSources(story.feedSources)}
                                </span>
                            </>
                        )}
                        {formatShares(story.totalShares) && (
                            <span className="sf-row-meta-item">
                                📤 {formatShares(story.totalShares)}
                            </span>
                        )}
                        {story.articles?.length > 0 && (
                            <>
                                <span className="sf-row-meta-sep">|</span>
                                <span className="sf-row-meta-item">
                                    🕐 Updated {timeAgo(story.recent)} · First {timeAgo(story.earliest)}
                                </span>
                            </>
                        )}
                        {/* When the growth actually peaked, not when the hourly
                            run noticed it: the two differ by up to an hour and
                            this line is answering "when did this break". */}
                        {signal?.spottedAt && (
                            <>
                                <span className="sf-row-meta-sep">|</span>
                                <span className="sf-row-meta-item sf-row-meta-trending">
                                    Spotted {timeAgo(signal.spottedAt)}
                                    {signal.articlesInWindow && signal.windowMinutes
                                        ? ` · ${signal.articlesInWindow} in ${signal.windowMinutes} min`
                                        : ''}
                                </span>
                            </>
                        )}
                    </div>

                    {/* Action buttons row — order follows Figma: Preview, Share, Merge, Mark, Social, Copy */}
                    <div className="sf-row-actions-row">
                        <div className="sf-row-actions-left">
                            {/* Preview */}
                            {excerptText && (
                                <button className="sf-row-action-btn sf-row-action-btn-preview" onClick={handleShowPreview}>
                                    <span style={{ display: 'inline-flex', transition: 'transform 0.2s', transform: isPreviewVisible ? 'rotate(90deg)' : 'none' }}>
                                        <ChevronRight size={12} />
                                    </span>
                                    Preview
                                </button>
                            )}

                            {/* Share */}
                            {canEdit && (
                                <button className="sf-row-action-btn sf-row-action-btn-share" onClick={handleSharePublicStory}>
                                    <ShareIcon size={12} /> Share
                                </button>
                            )}

                            {/* Merge */}
                            {canEdit && onToggleMergeSelect && (
                                <button
                                    className={`sf-row-action-btn${isMergeSelected ? ' sf-row-action-btn-active' : ''}`}
                                    onClick={handleMergeSelect}
                                >
                                    <MergeIcon size={12} />
                                    Merge
                                </button>
                            )}

                            {/* Spec §16.2 — one click, no modal, no confirmation, no
                                explanation. Admin only, and only when a category
                                filter is active: the signal is scoped to a category,
                                so without one there is nothing to mark it against. */}
                            {isAdmin && activeCategory && onMarkIrrelevant && !isIrrelevant && (
                                <button
                                    className="sf-row-action-btn"
                                    title={`Remove this story from ${activeCategory} and record it as a signal`}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onMarkIrrelevant(story);
                                    }}
                                >
                                    <IrrelevantIcon size={12} />
                                    Irrelevant
                                </button>
                            )}

                            {/* Mark as completed / uncompleted */}
                            {canEdit && (
                                <button
                                    className={`sf-row-action-btn${localAlreadyDone ? ' sf-row-action-btn-active' : ''}`}
                                    onClick={handleMarkCompleted}
                                >
                                    <CheckIcon size={12} />
                                    {localAlreadyDone ? 'Mark as uncompleted' : 'Mark as completed'}
                                </button>
                            )}

                            {/* Create Social Post */}
                            {hasSocialPosts && story._id && (
                                <button
                                    className="sf-row-action-btn"
                                    onClick={handleCreateSocialPost}
                                    disabled={generatingSocialPost}
                                    style={generatingSocialPost ? { opacity: 0.6, cursor: 'wait' } : undefined}
                                >
                                    <ShareIcon size={12} />
                                    {generatingSocialPost ? 'Generating...' : 'Create social post'}
                                </button>
                            )}

                            {/* Add to bucket. The explicit alternative to
                                dragging the card onto the Publisher Buckets
                                panel; same write, same gates. Admin only
                                because POST /publisher-buckets/:id/stories is
                                verifySuperUser. */}
                            {isAdmin && (
                                <button className="sf-row-action-btn" onClick={handleOpenAddToBucket}>
                                    <BucketIcon size={12} /> Add to bucket
                                </button>
                            )}

                        </div>

                        {/* Article count on the right */}
                        {showArticleCount && (
                            <div className="sf-row-actions-count">
                                {storyCardArticleCount} {storyCardArticleCount === 1 ? 'article' : 'articles'}
                            </div>
                        )}
                    </div>

                    {/* Preview content */}
                    {isPreviewVisible && excerptText && (
                        <div className="sf-row-preview">
                            <div className="sf-row-preview-content">
                                {!isTranslating && story.rawExcerpt && !story.rawExcerpt.isEnglish && translatedExcerpt && (
                                    <span className="sf-row-preview-translated">(translated)</span>
                                )}
                                {!isTranslating && (
                                    <button
                                        className="sf-row-preview-copy"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            const textToCopy = translatedExcerpt || excerptText;
                                            navigator.clipboard.writeText(textToCopy)
                                                .then(() => { if (showToastNotification) showToastNotification('Preview copied to clipboard!'); })
                                                .catch(() => { if (showToastNotification) showToastNotification('Failed to copy preview', 'error'); });
                                        }}
                                        title="Copy preview"
                                    >
                                        <CopyIcon size={14} />
                                    </button>
                                )}
                                {isTranslating ? (
                                    <SkeletonLoader />
                                ) : (
                                    `"${translatedExcerpt || excerptText}"`
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>
            <span className="sf-row-count">{(story.totalArticleCount || story.articleCount || 0)} {(story.totalArticleCount || story.articleCount || 0) === 1 ? 'article' : 'articles'}</span>
            <button className="sf-row-share" onClick={(e) => { e.stopPropagation(); if (onShare) onShare(story); }} title="Share story">
                <ShareIcon size={16} />
            </button>
            <button className="sf-row-next" onClick={handleNext}>
                Next →
            </button>

            {/* Mounted only while open: the picker loads buckets on mount,
                and the feed renders one of these per story row. */}
            {addToBucketOpen && (
                <AddToBucketModal
                    open
                    story={story}
                    collectionScope={collectionScope}
                    onClose={() => setAddToBucketOpen(false)}
                    showToastNotification={showToastNotification}
                />
            )}
        </div>
    );
}

export default function StoriesFeed({
    stories,
    errorStrories,
    handleExpandStory,
    handleMergeStories,
    sortBy,
    isLoading,
    onLoadMore,
    hasMoreStories,
    isLoadingMore,
    expandedTimeRange,
    mergeStories: isMerging,
    showToastNotification,
    onIdleRefresh,
    currentStep,
    // Spec §16 — the category currently filtered. The Irrelevant signal is scoped
    // to a category, so with no filter there is nothing to mark a story against
    // and the action does not appear.
    activeCategory,
    onCategoryOptimizeTrigger,
    // The country/language filter in force, recorded on any suggestion added
    // from this feed. Null when nothing is filtered.
    collectionScope,
}) {
    const canEdit = useCanEdit();
    const isAdmin = useIsAdmin();
    // §16.2 — removed from the current feed IMMEDIATELY, optimistically. The
    // signal is durable server-side; re-fetching the whole feed to hide one row
    // would defeat "one click".
    const [irrelevantIds, setIrrelevantIds] = useState(new Set());
    // §16.4 — counted for this session only, and reset when the category changes:
    // "During the SAME category-based search session, count Irrelevant actions for
    // the ACTIVE category."
    const [sessionIrrelevantCount, setSessionIrrelevantCount] = useState(0);

    useEffect(() => {
        setIrrelevantIds(new Set());
        setSessionIrrelevantCount(0);
    }, [activeCategory]);

    const handleMarkIrrelevant = useCallback(async (story) => {
        if (!activeCategory || !story?._id) return;
        const nextCount = sessionIrrelevantCount + 1;
        // Optimistic: hide first, then persist. §16.2 requires no confirmation and
        // no waiting.
        setIrrelevantIds((prev) => new Set(prev).add(story._id));
        setSessionIrrelevantCount(nextCount);
        try {
            const res = await api.markStoryIrrelevantForCategory(activeCategory, story._id, {
                sessionIrrelevantCount: nextCount,
            });
            // §16.4 — after three, offer the lightweight Optimize category action.
            // Non-blocking, opens in a new tab, needs no written feedback first.
            if (res?.sessionTrigger?.show && onCategoryOptimizeTrigger) {
                onCategoryOptimizeTrigger(res.sessionTrigger);
            }
        } catch (err) {
            // Put it back rather than leaving the admin believing it was recorded.
            setIrrelevantIds((prev) => {
                const next = new Set(prev);
                next.delete(story._id);
                return next;
            });
            setSessionIrrelevantCount((c) => Math.max(0, c - 1));
            if (showToastNotification) showToastNotification('Could not mark that story as irrelevant.', 'error');
        }
    }, [activeCategory, sessionIrrelevantCount, onCategoryOptimizeTrigger, showToastNotification]);
    const { isEntitled: hasSocialPosts } = useHasFeature('social-posts-creation');
    const { STORIES_IDLE_REFRESH_MS } = useConfig();
    const { selectedTimeRange } = useSelector(selectSearchParams);
    const debugIdleRefresh = useMemo(() => {
        try { return new URLSearchParams(window.location.search).get('debugIdleRefresh') === '1'; }
        catch { return false; }
    }, []);
    const previousTitleRef = useRef('');
    const [animationKey, setAnimationKey] = useState(0);
    const [alreadyDoneIds, setAlreadyDoneIds] = useState([]);
    const [expandedIds, setExpandedIds] = useState(new Set());
    const [mergeSelectedIds, setMergeSelectedIds] = useState(new Set());
    const expandAllTrigger = useSelector((state) => state.articles.expandAllTrigger);
    const rdDispatch = useDispatch();
    const sentinelRef = useRef(null);
    const listRef = useRef(null);
    const pendingScrollRef = useRef(null);

    const handleExpandStoryWithScroll = useCallback((story) => {
        if (listRef.current) {
            pendingScrollRef.current = listRef.current.scrollTop;
        }
        handleExpandStory(story);
    }, [handleExpandStory]);

    useEffect(() => {
        if (pendingScrollRef.current === null) return;
        const el = listRef.current;
        if (!el || el.offsetParent === null) return;
        if (el.scrollHeight <= el.clientHeight) return;
        const pos = pendingScrollRef.current;
        pendingScrollRef.current = null;
        el.scrollTop = pos;
    });

    // Toggle all expand/collapse when sidebar button is clicked
    const expandedRef = useRef(false);
    useEffect(() => {
        if (expandAllTrigger === 0) return;
        if (expandedRef.current) {
            setExpandedIds(new Set());
            expandedRef.current = false;
            rdDispatch(setStoriesAllExpanded(false));
        } else {
            const allIds = new Set(stories.filter(s => s.groupSubject && s.groupSubject._id).map(s => s.groupSubject._id));
            setExpandedIds(allIds);
            expandedRef.current = true;
            rdDispatch(setStoriesAllExpanded(true));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [expandAllTrigger]);


    // Fetch already-done stories once on mount
    useEffect(() => {
        if (new URLSearchParams(window.location.search).get('designFixture') === '1') return;
        api.getAlreadyDoneStories()
            .then(ids => setAlreadyDoneIds(ids || []))
            .catch(() => {});
    }, []);

    // Build the display list from raw stories
    const storiesList = useMemo(() => {
        // The mapping is a STRICT WHITELIST and lives in feedStoryShape.js so it
        // can be tested without rendering this component. It was inline and
        // untested, which is how the Trending marker reached production dead:
        // the server sent `signal`, the row read it, and this step dropped it.
        let list = stories
            .filter(s => s.groupSubject && s.groupSubject._id)
            .map(mapFeedStory);
        if (sortBy === 'recent') {
            list.sort((a, b) => new Date(b.recent) - new Date(a.recent));
        }
        return list;
    }, [stories, sortBy]);

    // Idle feed refresh
    const idleRefreshPendingRef = useRef(false);

    const handleIdleRefreshCb = useCallback(() => {
        if (!onIdleRefresh) return;
        const result = onIdleRefresh();
        if (result === false) {
            if (debugIdleRefresh) console.debug('[IdleRefresh] refresh skipped, fetch already active');
            return;
        }
        if (debugIdleRefresh) console.debug('[IdleRefresh] idle refresh triggered');
        idleRefreshPendingRef.current = true;
    }, [onIdleRefresh, debugIdleRefresh]);

    useIdleCallback({
        enabled: currentStep === 0 && !isLoading && storiesList.length > 0 && !!onIdleRefresh,
        idleMs: Number(STORIES_IDLE_REFRESH_MS) || 300000,
        onIdle: handleIdleRefreshCb,
    });

    useEffect(() => {
        if (!idleRefreshPendingRef.current) return;
        idleRefreshPendingRef.current = false;
        const hash = window.location.hash;
        if (hash) {
            requestAnimationFrame(() => {
                const el = document.getElementById(hash.slice(1));
                if (el) {
                    el.scrollIntoView({ block: 'nearest' });
                    if (debugIdleRefresh) console.debug(`[IdleRefresh] anchor restored: ${hash}`);
                } else if (debugIdleRefresh) {
                    console.debug('[IdleRefresh] scroll position preserved (anchor not found)');
                }
            });
        } else if (debugIdleRefresh) {
            console.debug('[IdleRefresh] scroll position preserved');
        }
    }, [storiesList, debugIdleRefresh]);

    // Live mode flash animation
    useEffect(() => {
        if (sortBy === 'recent' && storiesList.length && previousTitleRef.current !== storiesList[0].title) {
            setAnimationKey(k => k + 1);
            previousTitleRef.current = storiesList[0].title;
        }
    }, [storiesList, sortBy]);

    // Infinite scroll via IntersectionObserver
    useEffect(() => {
        if (!sentinelRef.current || !onLoadMore) return;
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting && hasMoreStories && !isLoadingMore) {
                    onLoadMore();
                }
            },
            { rootMargin: '200px' }
        );
        observer.observe(sentinelRef.current);
        return () => observer.disconnect();
    }, [hasMoreStories, isLoadingMore, onLoadMore]);

    const handleShare = useCallback((story) => {
        const url = `${window.location.origin}/s/${story.slug}`;
        const text = `${story.title}\n${url}`;
        navigator.clipboard.writeText(text).then(() => {
            if (showToastNotification) showToastNotification('Story link copied!', 'success');
        }).catch(() => {});
    }, [showToastNotification]);

    const handleToggleExpand = useCallback((storyId) => {
        setExpandedIds(prev => {
            const next = new Set(prev);
            if (next.has(storyId)) next.delete(storyId);
            else next.add(storyId);
            return next;
        });
    }, []);

    const handleToggleMergeSelect = useCallback((story) => {
        setMergeSelectedIds(prev => {
            const next = new Set(prev);
            if (next.has(story._id)) next.delete(story._id);
            else next.add(story._id);
            return next;
        });
    }, []);

    const handleExecuteMerge = useCallback(() => {
        const selectedStories = storiesList.filter(s => mergeSelectedIds.has(s._id));
        if (selectedStories.length >= 2 && handleMergeStories) {
            handleMergeStories(selectedStories);
            setMergeSelectedIds(new Set());
        }
    }, [storiesList, mergeSelectedIds, handleMergeStories]);

    const handleCancelMerge = useCallback(() => {
        setMergeSelectedIds(new Set());
    }, []);

    const isFixture = new URLSearchParams(window.location.search).get('designFixture') === '1';
    const feedTitle = useMemo(() => {
        if (isFixture || selectedTimeRange === 'Live') return 'Explore top trending news';
        return 'Explore top trending news';
    }, [selectedTimeRange, isFixture]);

    // The header stays mounted through loading so the page does not collapse and
    // re-expand on every filter change.
    const feedHeader = (
        <div className="sf-center-header">
            <div className="sf-center-header-left">
                <h1 className="sf-title">{feedTitle}</h1>
                <div className="sf-status">
                    <span className="sf-status-dot" />
                    {isLoading || isMerging ? 'Updating\u2026' : 'Updated just now'}
                </div>
            </div>
        </div>
    );

    // Loading state
    if ((isLoading || isMerging) && selectedTimeRange !== 'Live') {
        return (
            <div style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
                {feedHeader}
                <div className="sf-stories-list">
                    <StoriesSkeleton rows={8} />
                </div>
            </div>
        );
    }

    // Error state. A plain string is an unexpected failure; an object carries a
    // title the backend could explain (e.g. a search that exceeded its time
    // budget), which should not be announced as "Error loading stories".
    if (errorStrories) {
        const { title, message } = typeof errorStrories === 'string'
            ? { title: 'Error loading stories', message: errorStrories }
            : errorStrories;
        return (
            <div className="sf-empty">
                <div className="sf-empty-title">{title}</div>
                <div className="sf-empty-subtitle">{message}</div>
            </div>
        );
    }

    // Empty state
    if (!isLoading && storiesList.length === 0) {
        return (
            <div className="sf-empty">
                <img src="/not-found.svg" alt="No stories found" />
                <div className="sf-empty-title">No Stories Found</div>
                <div className="sf-empty-subtitle">Try adjusting your filters or time range</div>
            </div>
        );
    }

    return (
        <div style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
            {/* Header */}
            {feedHeader}

            {/* Merge toolbar */}
            {mergeSelectedIds.size > 0 && (
                <div className="sf-merge-toolbar">
                    <span className="sf-merge-count">{mergeSelectedIds.size} stories selected</span>
                    <button className="sf-merge-btn" onClick={handleExecuteMerge} disabled={mergeSelectedIds.size < 2}>
                        Merge Selected
                    </button>
                    <button className="sf-merge-cancel" onClick={handleCancelMerge}>Cancel</button>
                </div>
            )}

            {/* Expanded time range banner */}
            {expandedTimeRange && (
                <div className="sf-expanded-banner">
                    <span>ℹ</span>
                    <span>No stories found in the selected time range. Showing stories from <strong>{expandedTimeRange}</strong> instead.</span>
                </div>
            )}

            {/* Stories list */}
            <div className="sf-stories-list" ref={listRef}>
                {storiesList.map((story, i) => (
                    <StoryRow
                        key={story._id}
                        story={story}
                        collectionScope={collectionScope}
                        onExpand={handleExpandStoryWithScroll}
                        onShare={handleShare}
                        onMerge={handleMergeStories}
                        canEdit={canEdit}
                        isAdmin={isAdmin}
                        isNew={i === 0 && animationKey > 0 && sortBy === 'recent'}
                        alreadyDone={alreadyDoneIds.includes(story._id)}
                        isExpanded={expandedIds.has(story._id)}
                        onToggleExpand={handleToggleExpand}
                        isMergeSelected={mergeSelectedIds.has(story._id)}
                        onToggleMergeSelect={handleToggleMergeSelect}
                        showToastNotification={showToastNotification}
                        hasSocialPosts={hasSocialPosts}
                        activeCategory={activeCategory}
                        onMarkIrrelevant={handleMarkIrrelevant}
                        isIrrelevant={irrelevantIds.has(story._id)}
                    />
                ))}

                {/* Infinite scroll sentinel */}
                <div ref={sentinelRef} className="sf-load-more">
                    {isLoadingMore && <StoriesSkeleton rows={2} fadeLast={false} />}
                </div>
                <div className="sf-footer-spacer" />
            </div>
        </div>
    );
}
